import React from 'react';
import FullPageLoader from '../Component/Loader/FullPageLoader';
import DownloadLoader from '../Component/Loader/DownloadLoader';
import { OfflineSaveProgress, subscribeOfflineSave } from '../Service/OfflineSaveService';
import { KmlDownloadProgress, subscribeKmlDownload } from '../Service/KmlDownloadService';

interface LoaderContextType {
  showLoader: boolean;
  setShowLoader: (show: boolean) => void;
}

export const LoaderContext = React.createContext<LoaderContextType>({
  showLoader: false,
  setShowLoader: () => {},
});

// The save job reports after every photo: subscribing here keeps those renders inside the overlay, instead of
// re-rendering the page behind it (the provider's own state never changes while a save runs)
const OfflineSaveOverlay: React.FC = () => {
  const [offlineSave, setOfflineSave] = React.useState<OfflineSaveProgress | null>(null);

  React.useEffect(() => subscribeOfflineSave(setOfflineSave), []);

  return offlineSave
    ? <DownloadLoader title='Saving the trees for offline use' {...offlineSave} />
    : null;
};

// The KML download reports the bytes downloaded as they arrive
const KmlDownloadOverlay: React.FC = () => {
  const [kmlDownload, setKmlDownload] = React.useState<KmlDownloadProgress | null>(null);

  React.useEffect(() => subscribeKmlDownload(setKmlDownload), []);

  return kmlDownload
    ? <DownloadLoader title='Saving the KML file' {...kmlDownload} />
    : null;
};

export const LoaderProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [showLoader, setShowLoader] = React.useState(false);
  const value = React.useMemo(() => ({ showLoader, setShowLoader }), [showLoader]);

  return (
    <LoaderContext.Provider value={value}>
      {showLoader && <FullPageLoader />}
      <OfflineSaveOverlay />
      <KmlDownloadOverlay />
      {children}
    </LoaderContext.Provider>
  );
};
